/**
 * Validate product size seleciont and format product size
 */

import { useProductPageStore } from "../stores/productPageStore";

export const validateRegularSizeSelection = (productPageStore: ReturnType<typeof useProductPageStore>) => {
  if (!productPageStore.sizeSelected) {
    productPageStore.displaySizeNotSelectedError = true;
    return false;
  }
  productPageStore.displaySizeNotSelectedError = false;
  return true;
};

export const validatePantsSizeSelection = (productPageStore: ReturnType<typeof useProductPageStore>) => {
  if (productPageStore.widthSelected && productPageStore.lengthSelected) {
    productPageStore.displayWidthNotSelectedError = false;
    productPageStore.displayLengthNotSelectedError = false;
    return true;
  }

  if (!productPageStore.widthSelected) {
    productPageStore.displayWidthNotSelectedError = true;
  }
  if (!productPageStore.lengthSelected) {
    productPageStore.displayLengthNotSelectedError = true;
  }
  return false;
};

export const validateSuitSizeSelection = (productPageStore: ReturnType<typeof useProductPageStore>) => {
  const { sizeSelected, widthSelected, lengthSelected } = productPageStore;

  productPageStore.displaySizeNotSelectedError = !sizeSelected;
  productPageStore.displayWidthNotSelectedError = !widthSelected;
  productPageStore.displayLengthNotSelectedError = !lengthSelected;

  return sizeSelected && widthSelected && lengthSelected;
};

export const validateSizeSelection = (sizePickerType: string, productPageStore: ReturnType<typeof useProductPageStore>) => {
  switch (sizePickerType) {
    case "suit":
      return validateSuitSizeSelection(productPageStore);
    case "pants":
      return validatePantsSizeSelection(productPageStore);
    case "regular":
      return validateRegularSizeSelection(productPageStore);
    default:
      return false;
  }
};

function formatZeroSize(size: string) {
    // If 0 return onesize
    if (size === "0") {
        return "ONESIZE";
    }
    return null;
}

function formatSockSize(size: string) {
    const sockSizes = [
        "3639",
        "3641",
        "3842",
        "3941",
        "4045",
        "4046",
        "4145",
        "4247",
    ];

    if (sockSizes.includes(size)) {
        return size.slice(0, 2) + "-" + size.slice(2); // Format as range
    }

    return null; // Return null if it's not a sock size
}

// Function to format regular sizes
function formatRegularSize(size: string): string | null {
    const regularSizes = [
        "XXS",
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "XXL",
        "XXXL",
        "XXXXL",
    ];

    if (regularSizes.includes(size)) {
        const regex = /^(X{2,})(L|S)$/;
        const match = size.match(regex);

        if (match) {
            // Count the number of 'X's and append the last character (L or S)
            const count = match[1].length;
            return `${count}X${match[2]}`;
        }
        return size; // Return regular sizes as they are
    }
    return null; // Return null if it's not a regular size
}

// Function to format pants sizes
function formatPantsSize(size: string): string | null {
    // Case 1: Standard numeric pants sizes (e.g., "3032" → "30/32")
    if (/^\d{4}$/.test(size)) {
        return size.slice(0, 2) + "/" + size.slice(2);
    }

    // Case 2: Letter-based width pants sizes (e.g., "L30" → "L/30", "XS30" → "XS/30")
    const match = size.match(/^(XXS|XS|S|M|L|XL|XXL|XXXL|XXXXL)(\d{2})$/);
    if (match) {
        return `${match[1]}/${match[2]}`;
    }

    return null; // Return null if it's not a pants size
}

// Function to format suit sizes
function formatSuitSize(
    size: string,
    isRegularSizePicker?: boolean,
): string | null {
    const suitPattern = /^([BC])([123])(\d{2})$/;
    const match = suitPattern.exec(size);

    if (match) {
        const width = match[1] === "B" ? "Normal vidde" : "Ekstra vidde";
        const length =
            match[2] === "1"
                ? "kort lengde"
                : match[2] === "2"
                  ? "normal lengde"
                  : "lang lengde";
        const sizeNumber = match[3];
        //if by chance a suit size has been translated to a regularSize from backend return only the size
        if (isRegularSizePicker) {
            return sizeNumber;
        }
        return `${width}, ${length}, ${sizeNumber}, (${size})`;
    }
    return null; // Return null if it's not a suit size
}

// Main size formatting function
export function formatSize(
    size: string,
    isRegularSizePicker?: boolean,
): string {
    // If 0 return ONESIZE
    const zeroSize = formatZeroSize(size);
    if (zeroSize) {
        return zeroSize;
    }

    // Check for sock size
    const sockSize = formatSockSize(size);
    if (sockSize) {
        return sockSize;
    }

    // Check for regular size
    const regularSize = formatRegularSize(size);
    if (regularSize) {
        return regularSize;
    }

    // Check for pants size
    const pantsSize = formatPantsSize(size);
    if (pantsSize) {
        return pantsSize;
    }

    // Check for suit size
    const suitSize = formatSuitSize(size, isRegularSizePicker);
    if (suitSize) {
        return suitSize;
    }

    // Return the original string if it doesn't match any of the patterns
    return size;
}

export const isSuitSize = (size: string) => {
    return formatSuitSize(size) !== null;
};

export const isRegularSize = (size: string) => {
    return formatRegularSize(size) !== null;
};

export const formatFilterSize = (size: string) => {
    if (isSuitSize(size)) {
        return size;
    }

    return formatSize(size);
};